import { useForm, useFieldArray, useFormContext } from "react-hook-form";
import { Form } from "../../components/shadcn-ui/form";
import { Card } from "../../components/shadcn-ui/card";
import { Button } from "../../components/shadcn-ui/button";
import { Input } from "../../components/shadcn-ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/shadcn-ui/select";
import { useToast } from "@customHooks/useToast";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { createGroup } from "../../features/groups/services/groupsService";
import { INDIAN_CITIES, CITY_MAP, INDIAN_STATES } from "features/loanApplications/formFieldsConfig/indiaStatesList";
import { createLead, checkLeadExists } from "../../features/leads/services/leadsService";
import { getContractorDetails, ContractorDetails } from "../../features/contractor/services/contractorService";
import { Separator } from "@components/shadcn-ui/separator";
import { Plus, Trash2, UserCheck, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { LEADS_FORM_FIELDS } from "features/loanApplications/formFieldsConfig";
import { DynamicFormField, FieldType } from "../../components/ui/DynamicInput";
import { isFieldAsCity } from "@lib/utils";
import { useAuth } from "context/AuthContext";

// Validation states for the two-step flow
type MemberValidationState =
    | 'INITIAL'           // Step 1: Phone + channel input
    | 'VALIDATING'        // Loading state
    | 'CONTRACTOR_FOUND'  // Step 2: Contractor preview shown
    | 'FORM'              // Step 3: Full form shown
    | 'ERROR';            // Error state

type MemberForm = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    state: string;
    postalCode: string;
    referrer: string;
    channel: string;
    pilId?: string;
    isCoordinator: boolean;
    leadExists: boolean;
    contactId?: string;
    originalData?: any;
    // New fields for two-step validation
    validationState: MemberValidationState;
    contractorData?: ContractorDetails;
    errorMessage?: string;
};

type FormData = {
    name: string;
    city: string;
    bdManagerName: string;
    bdManagerPhoneNumber: string;
    members: MemberForm[];
};

// Sourcing channel options
const SOURCING_CHANNELS = [
    "PIL BD",
    "PIL Sales",
    "PIL Contractor",
    "Dealer",
    "Website",
    "Existing Customer",
    "WhatsApp",
    "FCC",
    "Other",
    "Call Center"
];

/**
 * Maps contractor data to form fields
 * Splits contractor_name into firstName and lastName
 * Validates state and city against available options
 */
const mapContractorToFormData = (contractor: ContractorDetails): Partial<MemberForm> => {
    const names = contractor.contractor_name.trim().split(/\s+/);
    const firstName = names[0] || "";
    const lastName = names.length > 1 ? names.slice(1).join(" ") : firstName;

    // Validate state against INDIAN_STATES
    const state = INDIAN_STATES.includes(contractor.state) ? contractor.state : "";

    // Validate city against CITY_MAP for the selected state
    const citiesForState = state ? CITY_MAP[state] : [];
    const city = citiesForState?.includes(contractor.city) ? contractor.city : "";

    return {
        firstName,
        lastName,
        postalCode: contractor.pincode || "",
        state,
        city,
    };
};

/**
 * Formats the membership date for display
 */
const formatMemberSince = (dateString: string): string => {
    try {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch {
        return dateString;
    }
};

// Step 1: Phone and Channel Validation Component
const PhoneValidationStep = ({
    index,
    onValidate,
    onRemove,
    toggleCoordinator,
    fieldsLength
}: {
    index: number;
    onValidate: (index: number) => void;
    onRemove: (index: number) => void;
    toggleCoordinator: (index: number) => void;
    fieldsLength: number;
}) => {
    const form = useFormContext<FormData>();
    const member = form.watch(`members.${index}`);
    const isValidating = member?.validationState === 'VALIDATING';

    if (!member) return null;

    const phoneNumber = form.watch(`members.${index}.phoneNumber`);
    const channel = form.watch(`members.${index}.channel`);
    const isValidPhone = phoneNumber?.length === 10 && /^\d{10}$/.test(phoneNumber);
    const canValidate = isValidPhone && channel && !isValidating;

    return (
        <Card className="p-4 mb-4">
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                    <h4 className="font-medium">Member {index + 1}</h4>
                    {member.isCoordinator && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center gap-1">
                            <UserCheck className="h-3 w-3" />
                            Coordinator
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    {!member.isCoordinator && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => toggleCoordinator(index)}
                        >
                            Make Coordinator
                        </Button>
                    )}
                    {fieldsLength > 1 && (
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => onRemove(index)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                    <label className="text-sm font-medium">Phone Number *</label>
                    <Input
                        placeholder="10-digit phone number"
                        maxLength={10}
                        {...form.register(`members.${index}.phoneNumber`, {
                            required: "Phone number is required",
                            pattern: { value: /^\d{10}$/, message: "Must be 10 digits" },
                        })}
                        disabled={isValidating}
                    />
                    {form.formState.errors.members?.[index]?.phoneNumber && (
                        <p className="text-red-500 text-xs mt-1">
                            {form.formState.errors.members?.[index]?.phoneNumber?.message}
                        </p>
                    )}
                </div>

                <div>
                    <label className="text-sm font-medium">Sourcing Channel *</label>
                    <Select
                        onValueChange={(value) => form.setValue(`members.${index}.channel`, value)}
                        value={member.channel}
                        disabled={isValidating}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select a channel" />
                        </SelectTrigger>
                        <SelectContent>
                            {SOURCING_CHANNELS.map((ch) => (
                                <SelectItem key={ch} value={ch}>
                                    {ch}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {form.formState.errors.members?.[index]?.channel && (
                        <p className="text-red-500 text-xs mt-1">
                            {form.formState.errors.members?.[index]?.channel?.message}
                        </p>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {member.validationState === 'ERROR' && member.errorMessage && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{member.errorMessage}</p>
                </div>
            )}

            <Button
                type="button"
                onClick={() => onValidate(index)}
                disabled={!canValidate}
                className="w-full md:w-auto"
            >
                {isValidating ? (
                    <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Validating...
                    </>
                ) : (
                    "Validate Contractor"
                )}
            </Button>
        </Card>
    );
};

// Step 2: Contractor Preview Component
const ContractorPreviewStep = ({
    index,
    onProceed,
    onReset,
    onRemove,
    toggleCoordinator,
    fieldsLength
}: {
    index: number;
    onProceed: (index: number) => void;
    onReset: (index: number) => void;
    onRemove: (index: number) => void;
    toggleCoordinator: (index: number) => void;
    fieldsLength: number;
}) => {
    const form = useFormContext<FormData>();
    const member = form.watch(`members.${index}`);

    if (!member || !member.contractorData) return null;

    const contractor = member.contractorData;

    return (
        <Card className="p-4 mb-4">
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                    <h4 className="font-medium">Member {index + 1}</h4>
                    {member.isCoordinator && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center gap-1">
                            <UserCheck className="h-3 w-3" />
                            Coordinator
                        </span>
                    )}
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Verified Contractor
                    </span>
                </div>
                <div className="flex gap-2">
                    {!member.isCoordinator && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => toggleCoordinator(index)}
                        >
                            Make Coordinator
                        </Button>
                    )}
                    {fieldsLength > 1 && (
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => onRemove(index)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            {/* Contractor Details Preview Card */}
            <div className="bg-gray-50 border rounded-lg p-4 mb-4">
                <h5 className="font-medium text-gray-700 mb-3">Contractor Details</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                        <span className="text-gray-500">Name:</span>
                        <p className="font-medium">{contractor.contractor_name}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">Phone:</span>
                        <p className="font-medium">{contractor.contractor_mobile_number}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">Membership #:</span>
                        <p className="font-medium">{contractor.membership_number}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">Member Since:</span>
                        <p className="font-medium">{formatMemberSince(contractor.member_since)}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">City:</span>
                        <p className="font-medium">{contractor.city || "-"}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">State:</span>
                        <p className="font-medium">{contractor.state || "-"}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">Pincode:</span>
                        <p className="font-medium">{contractor.pincode || "-"}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">Classification:</span>
                        <p className="font-medium">{contractor.contractor_classification || "-"}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">Club:</span>
                        <p className="font-medium">{contractor.clubname || "-"}</p>
                    </div>
                </div>
            </div>

            <div className="flex gap-2">
                <Button
                    type="button"
                    onClick={() => onProceed(index)}
                    className="btn-primary"
                >
                    Proceed to Form
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => onReset(index)}
                >
                    Change Phone Number
                </Button>
            </div>
        </Card>
    );
};

// Step 3: Full Form Component (Similar to existing MemberRow but with prepopulated data)
const MemberFormStep = ({
    index,
    onRemove,
    toggleCoordinator,
    fieldsLength
}: {
    index: number;
    onRemove: (index: number) => void;
    toggleCoordinator: (index: number) => void;
    fieldsLength: number;
}) => {
    const form = useFormContext<FormData>();
    const member = form.watch(`members.${index}`);

    if (!member) return null;

    const onPhoneChange = (index: number, val: string) => {
        const currentMember = form.getValues(`members.${index}`);
        if (currentMember.leadExists && val !== currentMember.phoneNumber) {
            form.setValue(`members.${index}.leadExists`, false);
            form.setValue(`members.${index}.contactId`, undefined);
            form.setValue(`members.${index}.originalData`, undefined);
        }
        form.setValue(`members.${index}.phoneNumber`, val);
    };

    return (
        <Card className="p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                    <h4 className="font-medium">Member {index + 1}</h4>
                    {member.isCoordinator && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center gap-1">
                            <UserCheck className="h-3 w-3" />
                            Coordinator
                        </span>
                    )}
                    {member.leadExists ? (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            ✓ Existing Lead
                        </span>
                    ) : member.contractorData ? (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                            New Lead (from Contractor)
                        </span>
                    ) : null}
                </div>
                <div className="flex gap-2">
                    {!member.isCoordinator && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => toggleCoordinator(index)}
                        >
                            Make Coordinator
                        </Button>
                    )}
                    {fieldsLength > 1 && (
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => onRemove(index)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            {/* All Lead Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {LEADS_FORM_FIELDS.map((leadField: FieldType, fieldIndex: number) => {
                    const fieldName = leadField.name === "city" ? "city" : leadField.name;
                    const fullFieldName = `members.${index}.${fieldName}` as any;

                    // Watch state for THIS specific member
                    const memberState = form.watch(`members.${index}.state`);

                    // Custom handleChange for state to reset city
                    const handleChange = leadField.name === "phoneNumber"
                        ? (_: string, value: any) => onPhoneChange(index, value)
                        : leadField.name === "state"
                            ? (name: string, value: any) => {
                                form.setValue(name as any, value);
                                form.setValue(`members.${index}.city`, "");
                            }
                            : undefined;

                    // Check if the field has a value in the existing lead data
                    const hasExistingValue = () => {
                        if (!member.originalData) return false;
                        if (leadField.name === "phoneNumber" || leadField.name === "channel") return false;

                        if (leadField.name === "addressLineOne" || leadField.name === "addressLineTwo" || leadField.name === "city" || leadField.name === "state" || leadField.name === "postalCode") {
                            return !!member.originalData.address?.[leadField.name];
                        }
                        return !!member.originalData[leadField.name];
                    };

                    // Phone number is always disabled in form step (already validated)
                    // For existing leads, disable fields with existing values
                    // For new leads from contractor, all fields are editable except phone
                    const isFieldDisabled =
                        leadField.name === "phoneNumber" || // Phone is always disabled after validation
                        (member.leadExists &&
                            leadField.name !== "channel" &&
                            hasExistingValue()) ||
                        leadField.name === "referrer" ||
                        (isFieldAsCity(leadField.name) && !memberState && !member.leadExists);

                    const dynamicOptions =
                        isFieldAsCity(leadField.name)
                            ? CITY_MAP[memberState] || []
                            : leadField.options || [];

                    return (
                        <div key={fieldIndex} className="w-full">
                            <DynamicFormField
                                field={{ ...leadField, name: fullFieldName, options: dynamicOptions } as FieldType}
                                isDisabled={isFieldDisabled}
                                handleChange={handleChange}
                                {...form.register(fullFieldName)}
                            />
                            {form.formState.errors.members?.[index]?.[fieldName as keyof MemberForm] && (
                                <p className="text-red-500 text-xs mt-1">
                                    {(form.formState.errors.members?.[index]?.[fieldName as keyof MemberForm] as any)?.message}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

// Main component that renders the appropriate step for each member
const MemberRow = ({
    index,
    onValidate,
    onProceed,
    onReset,
    onRemove,
    toggleCoordinator,
    fieldsLength
}: {
    index: number;
    onValidate: (index: number) => void;
    onProceed: (index: number) => void;
    onReset: (index: number) => void;
    onRemove: (index: number) => void;
    toggleCoordinator: (index: number) => void;
    fieldsLength: number;
}) => {
    const form = useFormContext<FormData>();
    const member = form.watch(`members.${index}`);

    if (!member) return null;

    switch (member.validationState) {
        case 'INITIAL':
        case 'VALIDATING':
        case 'ERROR':
            return (
                <PhoneValidationStep
                    index={index}
                    onValidate={onValidate}
                    onRemove={onRemove}
                    toggleCoordinator={toggleCoordinator}
                    fieldsLength={fieldsLength}
                />
            );
        case 'CONTRACTOR_FOUND':
            return (
                <ContractorPreviewStep
                    index={index}
                    onProceed={onProceed}
                    onReset={onReset}
                    onRemove={onRemove}
                    toggleCoordinator={toggleCoordinator}
                    fieldsLength={fieldsLength}
                />
            );
        case 'FORM':
            return (
                <MemberFormStep
                    index={index}
                    onRemove={onRemove}
                    toggleCoordinator={toggleCoordinator}
                    fieldsLength={fieldsLength}
                />
            );
        default:
            return null;
    }
};

export function CreateGroupPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { userDetails } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getDefaultMember = (isCoordinator: boolean = false): MemberForm => ({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        addressLineOne: "",
        addressLineTwo: "",
        city: "",
        state: "",
        postalCode: "",
        referrer: userDetails?.email || "",
        channel: "",
        pilId: "",
        isCoordinator,
        leadExists: false,
        validationState: 'INITIAL',
    });

    const form = useForm<FormData>({
        mode: "all",
        defaultValues: {
            name: "",
            city: "",
            bdManagerName: "",
            bdManagerPhoneNumber: "",
            members: [getDefaultMember(true)],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: "members",
    });

    // Handle contractor validation (Step 1 -> Step 2 or Error)
    const onValidate = async (index: number) => {
        const phoneNumber = form.getValues(`members.${index}.phoneNumber`);
        const channel = form.getValues(`members.${index}.channel`);

        if (!phoneNumber || phoneNumber.length !== 10 || !channel) {
            return;
        }

        // Set validating state
        form.setValue(`members.${index}.validationState`, 'VALIDATING');
        form.setValue(`members.${index}.errorMessage`, undefined);

        try {
            // Step 1: Check contractor API
            const contractorData = await getContractorDetails(phoneNumber);

            if (!contractorData) {
                // Contractor not found - show error
                form.setValue(`members.${index}.validationState`, 'ERROR');
                form.setValue(
                    `members.${index}.errorMessage`,
                    "The phone number does not belong to a registered contractor. Please enter a valid contractor phone number to proceed."
                );
                return;
            }

            // Store contractor data
            form.setValue(`members.${index}.contractorData`, contractorData);
            form.setValue(`members.${index}.validationState`, 'CONTRACTOR_FOUND');

        } catch (error) {
            console.error("Error validating contractor:", error);
            form.setValue(`members.${index}.validationState`, 'ERROR');
            form.setValue(
                `members.${index}.errorMessage`,
                "An error occurred while validating the contractor. Please try again."
            );
        }
    };

    // Handle proceed from contractor preview (Step 2 -> Step 3)
    const onProceed = async (index: number) => {
        const phoneNumber = form.getValues(`members.${index}.phoneNumber`);
        const contractorData = form.getValues(`members.${index}.contractorData`);

        try {
            // Check if lead exists in contacts database
            const lead = await checkLeadExists(phoneNumber) as any;

            if (lead) {
                // Check if lead is already a member of a group
                if (lead.metaData?.memberOf) {
                    toast({
                        variant: "destructive",
                        title: "Lead already in a group",
                        description: `${lead.firstName} is already a member of "${lead.metaData.memberOf.name}"`,
                    });
                    form.setValue(`members.${index}.validationState`, 'ERROR');
                    form.setValue(
                        `members.${index}.errorMessage`,
                        `This person is already a member of group "${lead.metaData.memberOf.name}". Please use a different phone number.`
                    );
                    return;
                }

                // Lead exists - populate from lead data
                form.setValue(`members.${index}.firstName`, lead.firstName || "");
                form.setValue(`members.${index}.lastName`, lead.lastName || "");
                form.setValue(`members.${index}.email`, lead.email || "");

                if (lead.address) {
                    form.setValue(`members.${index}.addressLineOne`, lead.address.addressLineOne || "");
                    form.setValue(`members.${index}.addressLineTwo`, lead.address.addressLineTwo || "");
                    form.setValue(`members.${index}.state`, lead.address.state || "");
                    const cityValue = lead.address.city;
                    if (cityValue) {
                        setTimeout(() => {
                            form.setValue(`members.${index}.city`, cityValue);
                        }, 0);
                    }
                    form.setValue(`members.${index}.postalCode`, lead.address.postalCode || "");
                }

                if (lead.source) {
                    form.setValue(`members.${index}.channel`, lead.source.channel || "");
                }

                form.setValue(`members.${index}.leadExists`, true);
                form.setValue(`members.${index}.contactId`, lead.id);
                form.setValue(`members.${index}.originalData`, lead);

                toast({
                    description: `Existing lead found for ${phoneNumber}!`,
                });
            } else if (contractorData) {
                // Lead doesn't exist - populate from contractor data
                const mappedData = mapContractorToFormData(contractorData);
                form.setValue(`members.${index}.firstName`, mappedData.firstName || "");
                form.setValue(`members.${index}.lastName`, mappedData.lastName || "");
                form.setValue(`members.${index}.state`, mappedData.state || "");
                if (mappedData.city) {
                    setTimeout(() => {
                        form.setValue(`members.${index}.city`, mappedData.city || "");
                    }, 0);
                }
                form.setValue(`members.${index}.postalCode`, mappedData.postalCode || "");
                form.setValue(`members.${index}.leadExists`, false);
            }

            form.setValue(`members.${index}.validationState`, 'FORM');

        } catch (error) {
            console.error("Error checking lead:", error);
            // Even if lead check fails, proceed with contractor data
            if (contractorData) {
                const mappedData = mapContractorToFormData(contractorData);
                form.setValue(`members.${index}.firstName`, mappedData.firstName || "");
                form.setValue(`members.${index}.lastName`, mappedData.lastName || "");
                form.setValue(`members.${index}.state`, mappedData.state || "");
                if (mappedData.city) {
                    setTimeout(() => {
                        form.setValue(`members.${index}.city`, mappedData.city || "");
                    }, 0);
                }
                form.setValue(`members.${index}.postalCode`, mappedData.postalCode || "");
            }
            form.setValue(`members.${index}.validationState`, 'FORM');
        }
    };

    // Reset member back to initial state
    const onReset = (index: number) => {
        const isCoordinator = form.getValues(`members.${index}.isCoordinator`);
        const channel = form.getValues(`members.${index}.channel`);

        // Reset most fields but keep coordinator status and channel
        form.setValue(`members.${index}`, {
            ...getDefaultMember(isCoordinator),
            channel,
        });
    };

    const toggleCoordinator = (index: number) => {
        fields.forEach((_, i) => {
            form.setValue(`members.${i}.isCoordinator`, false);
        });
        form.setValue(`members.${index}.isCoordinator`, true);
    };

    const onSubmit = async (formState: FormData) => {
        // Validate all members are in FORM state
        const allMembersReady = formState.members.every(m => m.validationState === 'FORM');
        if (!allMembersReady) {
            toast({
                variant: "destructive",
                description: "Please complete contractor validation for all members before creating the group.",
            });
            return;
        }

        // Validate at least one coordinator
        const hasCoordinator = formState.members.some(m => m.isCoordinator);
        if (!hasCoordinator) {
            toast({
                variant: "destructive",
                description: "Please select at least one coordinator",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            // Process all members - create leads if needed and get contactIds
            const processedMembers = await Promise.all(
                formState.members.map(async (member) => {
                    let contactId = member.contactId;

                    // Create lead if doesn't exist
                    if (!member.leadExists) {
                        const leadData = {
                            firstName: member.firstName,
                            lastName: member.lastName,
                            phoneNumber: member.phoneNumber,
                            email: member.email,
                            address: {
                                addressLineOne: member.addressLineOne,
                                addressLineTwo: member.addressLineTwo,
                                city: member.city,
                                state: member.state,
                                postalCode: member.postalCode,
                            },
                            referral: member.referrer,
                            channel: member.channel,
                            pilId: member.pilId,
                        };

                        const createdLead = await createLead(leadData) as any;
                        contactId = createdLead.id;
                    }

                    return {
                        contactId: contactId!,
                        lastName: member.lastName,
                        phoneNumber: member.phoneNumber,
                        role: member.isCoordinator ? ("COORDINATOR" as const) : ("MEMBER" as const),
                    };
                })
            );

            // Create group with all members
            const coordinator = processedMembers.find(m => m.role === "COORDINATOR");
            if (!coordinator) {
                throw new Error("No coordinator found in members");
            }

            await createGroup({
                name: formState.name,
                city: formState.city,
                coordinator: coordinator as { contactId: string; lastName: string; phoneNumber: string; role: "COORDINATOR" },
                members: processedMembers,
                bdManager: {
                    name: formState.bdManagerName,
                    phoneNumber: formState.bdManagerPhoneNumber,
                },
                metaData: {
                    type: "loan_group",
                    desc: ""
                }
            });

            toast({
                title: "Group created successfully!",
                description: `Group "${formState.name}" with ${formState.members.length} member(s)`,
            });
            navigate("/dashboard/groups");
        } catch (err: any) {
            console.error("Error creating group:", err);
            toast({
                variant: "destructive",
                description: err?.message || "Failed to create group.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <strong className="block mt-0 mb-2 text-xl bg-blue-200 p-2 pl-4 rounded-sm">
                Create Group
            </strong>
            <Card className="p-4 h-full md:pt-6 overflow-auto">
                <div className="flex flex-col md:pl-4 flex-wrap">
                    <div className="w-full">
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)}>
                                {/* Group Details Section */}
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold mb-4">Group Information</h3>
                                    <div className="flex flex-col gap-y-4 max-w-2xl">
                                        <div>
                                            <label className="text-sm font-medium">Group Name *</label>
                                            <Input
                                                placeholder="Enter group name"
                                                {...form.register("name", {
                                                    required: "Group name is required",
                                                    minLength: { value: 3, message: "Min 3 characters" },
                                                })}
                                            />
                                            {form.formState.errors.name && (
                                                <p className="text-red-500 text-xs mt-1">{form.formState.errors.name.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium">Group City *</label>
                                            <Select
                                                onValueChange={(value) => form.setValue("city", value)}
                                                value={form.watch("city")}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select a city" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {INDIAN_CITIES.map((city) => (
                                                        <SelectItem key={city} value={city}>
                                                            {city}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {form.formState.errors.city && (
                                                <p className="text-red-500 text-xs mt-1">{form.formState.errors.city.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium">BD Manager Name *</label>
                                            <Input
                                                placeholder="Enter BD Manager name"
                                                {...form.register("bdManagerName", { required: "Required" })}
                                            />
                                            {form.formState.errors.bdManagerName && (
                                                <p className="text-red-500 text-xs mt-1">{form.formState.errors.bdManagerName.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium">BD Manager Phone Number *</label>
                                            <Input
                                                placeholder="10-digit phone number"
                                                maxLength={10}
                                                {...form.register("bdManagerPhoneNumber", {
                                                    required: "Required",
                                                    pattern: { value: /^\d{10}$/, message: "Must be 10 digits" },
                                                })}
                                            />
                                            {form.formState.errors.bdManagerPhoneNumber && (
                                                <p className="text-red-500 text-xs mt-1">{form.formState.errors.bdManagerPhoneNumber.message}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <Separator className="my-6" />

                                {/* Members Section */}
                                <div className="mb-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold">Group Members</h3>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => append(getDefaultMember(false))}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Member
                                        </Button>
                                    </div>

                                    {fields.map((field, index) => (
                                        <MemberRow
                                            key={field.id}
                                            index={index}
                                            onValidate={onValidate}
                                            onProceed={onProceed}
                                            onReset={onReset}
                                            onRemove={remove}
                                            toggleCoordinator={toggleCoordinator}
                                            fieldsLength={fields.length}
                                        />
                                    ))}
                                </div>

                                <div className="flex gap-2 mt-6">
                                    <Button type="submit" className="btn-primary" disabled={isSubmitting}>
                                        {isSubmitting ? "Creating..." : "Create Group"}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate("/dashboard/groups")}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    </div>
                </div>
            </Card>
        </>
    );
}
